export const CHUNK_RELOAD_FLAG = 'synopsis.insights.chunk-reload';

/** True for a lazy-chunk fetch failure (stale deploy serving index.html). */
export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  const msg = `${e?.name ?? ''} ${e?.message ?? String(err ?? '')}`;
  return (
    /ChunkLoadError/.test(msg) ||
    /Failed to fetch dynamically imported module/.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/.test(msg)
  );
}

/** Reload once per tab on a chunk error; returns true if a reload was triggered. */
export function reloadOnceForChunkError(
  err: unknown,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  reload: () => void
): boolean {
  if (!isChunkLoadError(err)) return false;
  try {
    if (storage?.getItem(CHUNK_RELOAD_FLAG)) return false;
    storage?.setItem(CHUNK_RELOAD_FLAG, '1');
  } catch {
    return false;
  }
  reload();
  return true;
}

/** Window listeners for unhandled chunk failures (router lazy loads reject here). */
export function installChunkReloadGuard(): void {
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  const handle = (err: unknown): boolean =>
    reloadOnceForChunkError(err, storage, () => window.location.reload());
  window.addEventListener('unhandledrejection', (e) => handle(e.reason));
  window.addEventListener('error', (e) => handle(e.error ?? e.message));
  // A healthy load later clears the flag so a future deploy can retry once.
  setTimeout(() => {
    try {
      storage?.removeItem(CHUNK_RELOAD_FLAG);
    } catch {
      /* ignore */
    }
  }, 10000);
}
